import React, { useEffect, useState } from 'react';
import { Button, Image, Text } from '@asigna/kit';
import { deleteContact } from 'api';
import { AddContact, OwnerCard, SkeletonContact } from 'components';
import { NotificationCard } from 'components/NotificationCard';
import { useActivateSafeFromUrlParam } from 'hooks';
import useNetwork from 'hooks/use-network';
import useData from 'hooks/useData';
import EmptyIcon from 'images/empty_contacts.png';
import addIcon from 'images/icon-add.svg';
import { useAtom } from 'jotai';
import { isEmpty } from 'lodash';
import { toast } from 'sonner';
import { contactsAtom } from 'store';
import { IContact } from 'types';

import { AddContactButton } from './components';

export const ContactsPage: React.FC = () => {
  useActivateSafeFromUrlParam();
  const [isAddContactOpened, setAddContactOpened] = useState(false);
  const [editingContact, setEditingContact] = useState<IContact | null>(null);
  const [loading, setLoading] = useState(false);
  const [contacts] = useAtom(contactsAtom);
  const { getContacts } = useData();
  const [network] = useNetwork();

  const fetchContacts = async () => {
    setLoading(true);
    await getContacts();
    setLoading(false);
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleClose = async (refresh: boolean) => {
    if (refresh) {
      await fetchContacts();
    }
    setAddContactOpened(false);
    setEditingContact(null);
  };

  const handleDelete = (id: string) => {
    setLoading(true);
    const contactName = contacts.find((contact) => contact._id === id)?.contact_name;
    deleteContact(id)
      .then(() => {
        fetchContacts();
        toast(
          () => (
            <NotificationCard
              title="Contact Removed"
              message={`Contact <b>${contactName}</b> removed successfully.`}
              type="success"
            />
          ),
          {
            className: 'w-full',
          }
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="flex w-full pr-0 sm:pr-24 pl-0 sm:pl-0 h-screen py-16">
      <div className="flex flex-col sm:rounded-[16px] sm:bg-black-1 w-full relative p-16 sm:p-32 overflow-y-scroll">
        <Text message="Contacts" color="white" size="r24" className="mb-20" />
        {loading && isEmpty(contacts) ? (
          <SkeletonContact />
        ) : contacts && contacts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-16 lg:gap-24 2xl:gap-32 flex-wrap">
            <AddContactButton
              onClick={() => {
                setAddContactOpened(true);
              }}
            />

            {contacts.map((contact, i) => {
              return (
                <OwnerCard
                  key={contact.contact_address}
                  address={contact.contact_address}
                  name={contact.contact_name}
                  safeId=""
                  addressWallet=""
                  network={network}
                  contact={contact}
                  onEdit={(contact) => setEditingContact(contact)}
                  onDelete={handleDelete}
                  index={i}
                />
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-16 mt-80">
            <Image src={EmptyIcon} size={110} />
            <Text message="No contacts yet" size="r16" color="white" />
            <Button
              colorType="secondary"
              onClick={() => setAddContactOpened(true)}
              leftIcon={addIcon}
              message="Add new contact"
            />
          </div>
        )}

        {(isAddContactOpened || editingContact) && (
          <AddContact
            open={isAddContactOpened || Boolean(editingContact)}
            onClose={handleClose}
            editingContact={editingContact}
            // TODO_R: лучше добавить onAddOrEditComplete prop и в нем вызывать fetchContacts
            fetchContacts={fetchContacts}
          />
        )}
      </div>
    </div>
  );
};
